import { useEffect } from 'react';

import { SearchFormProps } from '@occc/ui-design-system';
import { RetailerNetworkTobaccoProgramMetricType } from '@occc/graphql';
import { DateTimeFormat, formatDateTime } from '@occc-app/lib/date';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';

import { useRetailerNetworkTobaccoProgramSitesMetric } from './use-retailer-network-tobacco-program-site-metric';

import {
  LOCATION_RANKING_TABLE_AFTER_PARAM,
  LOCATION_RANKING_TABLE_BEFORE_PARAM,
  LOCATION_RANKING_TABLE_SEARCH_PARAM,
} from '../retailer-network-tobacco-performance-details-page.constants';

export type LocationRankingFilterValues = {
  search?: string;
  dateFrom: Date;
  dateTo: Date;
  retailerNetworkUuidEquals: string;
};

type LocationRankingOptions = {
  dateFrom: Date;
  dateTo: Date;
  retailerNetworkUuid: string;
  metricType: RetailerNetworkTobaccoProgramMetricType;
};

export const useLocationRanking = (options: LocationRankingOptions) => {
  const { dateFrom, dateTo, retailerNetworkUuid } = options;

  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: LOCATION_RANKING_TABLE_BEFORE_PARAM,
    afterParamName: LOCATION_RANKING_TABLE_AFTER_PARAM,
  });

  const [search, setSearch] = useSearchParams({
    searchParamName: LOCATION_RANKING_TABLE_SEARCH_PARAM,
    beforeParamName: LOCATION_RANKING_TABLE_BEFORE_PARAM,
    afterParamName: LOCATION_RANKING_TABLE_AFTER_PARAM,
  });

  const handleSearchSubmit: SearchFormProps['onSubmit'] = ({ search }) => {
    setSearch(search);
  };

  useEffect(() => {
    /**
     * Reset search and pagination when metric type changes
     */
    setSearch(undefined);
    // Ignoriing exhaustive-deps because of the setSearch is not stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.metricType]);

  const ranking = useRetailerNetworkTobaccoProgramSitesMetric({
    metricType: options.metricType,
    filter: {
      ...(!!search && { search }),
      dateFrom: formatDateTime(dateFrom, DateTimeFormat.DateDashed),
      dateTo: formatDateTime(dateTo, DateTimeFormat.DateDashed),
      retailerNetworkUuidEquals: retailerNetworkUuid,
    },
    paginationInfo,
    onPageChange,
  });

  return {
    onSearchSubmit: handleSearchSubmit,
    data: ranking.data,
    loading: ranking.loading,
    error: ranking.error,
    paginationProps: ranking.paginationProps,
  };
};
